import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
  DashboardCard,
  FilterBar,
  StatusBadge,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@zafroi/ui-kit';
import { useInvoices, useKoshLabels, useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { invoiceStatusVariants } from '@/config/kosh-status';
import { formatMoney, shortDate } from '@/lib/format';
import type { KoshInvoiceStatus } from '@/types/kosh';

const STATUS_FILTERS: (KoshInvoiceStatus | 'ALL')[] = [
  'ALL',
  'DRAFT',
  'ISSUED',
  'PARTIALLY_PAID',
  'PAID',
  'OVERDUE',
];

export function InvoicesPage() {
  const navigate = useNavigate();
  const q = useInvoices();
  const { labelPlural } = useKoshLabels();
  const nameMap = useCustomerNameMap();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<KoshInvoiceStatus | 'ALL'>('ALL');

  const invoices = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (status !== 'ALL' && inv.status !== status) return false;
      if (!s) return true;
      const name = nameMap.get(inv.customerId) ?? '';
      return inv.invoiceNumber.toLowerCase().includes(s) || name.toLowerCase().includes(s);
    });
  }, [invoices, search, status, nameMap]);

  const noun = labelPlural('invoice');

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Financial" title={noun} description={`All ${noun.toLowerCase()} — issue, track and settle.`} />
      <DashboardCard flushBody>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder={`Search ${noun.toLowerCase()}…`}
          />
          <div className="ml-auto inline-flex rounded-md border border-border p-0.5">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={
                  'rounded px-2.5 py-1 text-[12px] font-medium transition-colors ' +
                  (status === s
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:bg-muted')
                }
              >
                {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
        <QueryStateGuard
          data={filtered}
          isLoading={q.isLoading}
          isError={q.isError}
          emptyTitle={`No ${noun.toLowerCase()} found`}
        >
          {(rows) => (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead>Due</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((inv) => (
                  <TableRow
                    key={inv.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                  >
                    <TableCell className="font-mono text-[12px]">{inv.invoiceNumber}</TableCell>
                    <TableCell className="font-medium">
                      {nameMap.get(inv.customerId) ?? inv.customerId.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatMoney(inv.total, inv.currency)}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={inv.status} variants={invoiceStatusVariants} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{shortDate(inv.issueDate)}</TableCell>
                    <TableCell className="text-muted-foreground">{shortDate(inv.dueDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
